import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { CssBaseline, GlobalStyles, ThemeProvider } from '@mui/material';
import {
	THEME_MODE_KEY,
	createGofTheme,
	getCssVariables,
	isThemeMode,
	themeModes,
} from './themeTokens';

const ThemeModeContext = createContext({
	mode: themeModes.LIGHT,
	setThemeMode: () => {},
	toggleThemeMode: () => {},
});

const readStoredMode = () => {
	if (typeof window === 'undefined') {
		return themeModes.LIGHT;
	}

	try {
		const storedMode = window.localStorage.getItem(THEME_MODE_KEY);
		return isThemeMode(storedMode) ? storedMode : themeModes.LIGHT;
	} catch {
		return themeModes.LIGHT;
	}
};

const writeStoredMode = (mode) => {
	if (typeof window === 'undefined') {
		return;
	}

	try {
		window.localStorage.setItem(THEME_MODE_KEY, mode);
	} catch {
		// Theme preference is non-critical; rendering must continue without storage.
	}
};

export const ThemeModeProvider = ({ children }) => {
	const [mode, setMode] = useState(readStoredMode);

	useEffect(() => {
		if (typeof document !== 'undefined') {
			document.body.dataset.theme = mode;
		}
		writeStoredMode(mode);
	}, [mode]);

	const setThemeMode = useCallback((nextMode) => {
		if (isThemeMode(nextMode)) {
			setMode(nextMode);
		}
	}, []);

	const toggleThemeMode = useCallback(() => {
		setMode((currentMode) => (
			currentMode === themeModes.DARK ? themeModes.LIGHT : themeModes.DARK
		));
	}, []);

	const theme = useMemo(() => createGofTheme(mode), [mode]);
	const cssVariables = useMemo(() => ({
		body: getCssVariables(mode),
	}), [mode]);

	const contextValue = useMemo(() => ({
		mode,
		setThemeMode,
		toggleThemeMode,
	}), [mode, setThemeMode, toggleThemeMode]);

	return (
		<ThemeModeContext.Provider value={contextValue}>
			<ThemeProvider theme={theme}>
				<CssBaseline />
				<GlobalStyles styles={cssVariables} />
				{children}
			</ThemeProvider>
		</ThemeModeContext.Provider>
	);
};

export const useThemeMode = () => useContext(ThemeModeContext);
